import type { editor } from "monaco-editor";
import { useCallback, useEffect, useRef, useState } from "react";

import type { MatchEnvMap, Node } from "@acme/ast-grep-wasm-loader";
import type { AstGrepMatch } from "@acme/validators";

import { useMonacoEditor } from "./use-monaco-editor";

export function useSelectedAstNode(
  editorInstance: editor.IStandaloneCodeEditor | null,
  matchedHighlights: AstGrepMatch<MatchEnvMap>[],
  rootNode: Node | null,
) {
  const [selectedNodeInEditor, setSelectedNode] = useState<Node | null>(null);

  const matchDecorations = useRef<editor.IEditorDecorationsCollection | null>(
    null,
  );
  const treeHoverDecorations =
    useRef<editor.IEditorDecorationsCollection | null>(null);
  const matchHoverDecorations =
    useRef<editor.IEditorDecorationsCollection | null>(null);

  const { monacoEditorModule } = useMonacoEditor();
  const [hoveringNodeInTree, setHoveringNodeInTree] = useState<Node | null>(
    null,
  );
  const [hoveringNodeInMatchList, setHoveringNodeInMatchList] =
    useState<AstGrepMatch<MatchEnvMap> | null>(null);

  // Update selection handler to set selectedNode
  useEffect(() => {
    if (!editorInstance) return;

    const selectionChangeHandler = () => {
      const selection = editorInstance.getSelection();
      if (!selection || selection.isEmpty()) return;

      const startRow = selection.startLineNumber - 1;
      const startColumn = selection.startColumn - 1;
      const endRow = selection.endLineNumber - 1;
      const endColumn = selection.endColumn - 1;

      const node = findNodeForSelection(
        startRow,
        startColumn,
        endRow,
        endColumn,
        rootNode,
      );
      if (node) {
        setSelectedNode(node);
      }
    };

    const disposable = editorInstance.onDidChangeCursorSelection(
      selectionChangeHandler,
    );

    return () => {
      disposable.dispose();
    };
  }, [editorInstance, rootNode]);

  // Update click handler to set selectedNode
  useEffect(() => {
    if (!editorInstance) return;

    const editorDomNode = editorInstance.getDomNode();
    if (!editorDomNode) return;

    const clickHandler = (_event: MouseEvent) => {
      const position = editorInstance.getPosition();
      if (!position) return;

      const selection = editorInstance.getSelection();
      if (selection && !selection.isEmpty()) return;

      const row = position.lineNumber - 1;
      const column = position.column - 1;

      const node = findNodeAtPosition(row, column, rootNode);
      if (node) {
        setSelectedNode(node);
      }
    };

    editorDomNode.addEventListener("click", clickHandler);

    return () => {
      editorDomNode.removeEventListener("click", clickHandler);
    };
  }, [editorInstance, rootNode]);

  const clearSelectedNode = useCallback(() => {
    setSelectedNode(null);
  }, [setSelectedNode]);

  // Handle match decorations
  useEffect(() => {
    if (!editorInstance || !monacoEditorModule?.Range) return;

    if (matchDecorations.current) {
      matchDecorations.current.clear();
    }

    const transformMatch = (match: AstGrepMatch<MatchEnvMap>) => {
      const [sr, sc, er, ec] = match.range;
      return {
        range: new monacoEditorModule.Range(sr + 1, sc + 1, er + 1, ec + 1),
        options: {
          inlineClassName: "monaco-highlighted-match",
        },
      };
    };
    const decorations = matchedHighlights.map(transformMatch);

    matchDecorations.current =
      editorInstance.createDecorationsCollection(decorations);
  }, [editorInstance, matchedHighlights, monacoEditorModule?.Range]);

  // Handle tree hover decorations
  useEffect(() => {
    if (!editorInstance || !monacoEditorModule?.Range) return;

    if (treeHoverDecorations.current) {
      treeHoverDecorations.current.clear();
    }

    if (!hoveringNodeInTree && !hoveringNodeInMatchList) return;
    const [sr, sc, er, ec] = hoveringNodeInMatchList?.range ?? [
      hoveringNodeInTree?.start.row ?? 0,
      hoveringNodeInTree?.start.column ?? 0,
      hoveringNodeInTree?.end.row ?? 0,
      hoveringNodeInTree?.end.column ?? 0,
    ];

    const decorations = {
      range: new monacoEditorModule.Range(sr + 1, sc + 1, er + 1, ec + 1),
      options: {
        inlineClassName: hoveringNodeInMatchList?.id
          ? "monaco-match-hover"
          : "monaco-tree-hover",
      },
    };

    treeHoverDecorations.current = editorInstance.createDecorationsCollection([
      decorations,
    ]);
  }, [
    editorInstance,
    hoveringNodeInMatchList,
    hoveringNodeInTree,
    monacoEditorModule?.Range,
  ]);

  // Handle match hover decorations
  useEffect(() => {
    if (!editorInstance || !monacoEditorModule?.Range) return;

    if (matchHoverDecorations.current) {
      matchHoverDecorations.current.clear();
    }

    if (!hoveringNodeInMatchList) return;

    const transformHover = (match: AstGrepMatch<MatchEnvMap>) => {
      const [sr, sc, er, ec] = match.range;
      return {
        range: new monacoEditorModule.Range(sr + 1, sc + 1, er + 1, ec + 1),
        options: {
          inlineClassName: "monaco-match-hover",
        },
      };
    };

    const decorations = transformHover(hoveringNodeInMatchList);

    matchHoverDecorations.current = editorInstance.createDecorationsCollection([
      decorations,
    ]);
  }, [editorInstance, hoveringNodeInMatchList, monacoEditorModule?.Range]);

  return {
    selectedNodeInEditor,
    clearSelectedNode,
    setHoveringNodeInTree,
    setHoveringNodeInMatchList,
    hoveringNodeInTree,
    hoveringNodeInMatchList,
  };
}

const findNodeAtPosition = (
  row: number,
  column: number,
  rootNode: Node | null,
): Node | null => {
  if (!rootNode) return null;

  // Helper function to recursively search for a node containing the position
  const findNode = (node: Node): Node | null => {
    // Check if the position is within this node's range
    if (
      (node.start.row < row ||
        (node.start.row === row && node.start.column <= column)) &&
      (node.end.row > row ||
        (node.end.row === row && node.end.column >= column))
    ) {
      // Try to find a more specific child node that contains the position
      for (const child of node.children) {
        const foundNode = findNode(child);
        if (foundNode) return foundNode;
      }
      // If no child contains the position or there are no children, return this node
      return node;
    }
    return null;
  };

  return findNode(rootNode);
};

// Function to find the most relevant AST node for a text selection
const findNodeForSelection = (
  startRow: number,
  startColumn: number,
  endRow: number,
  endColumn: number,
  rootNode: Node | null,
): Node | null => {
  if (!rootNode) return null;

  // Helper function to calculate how well a node matches the selection
  const calculateMatchScore = (node: Node): number => {
    // Perfect match (node exactly matches selection)
    if (
      node.start.row === startRow &&
      node.start.column === startColumn &&
      node.end.row === endRow &&
      node.end.column === endColumn
    ) {
      return 100;
    }

    // Calculate overlap between node and selection
    const nodeStartPos = node.start.row * 10000 + node.start.column;
    const nodeEndPos = node.end.row * 10000 + node.end.column;
    const selectionStartPos = startRow * 10000 + startColumn;
    const selectionEndPos = endRow * 10000 + endColumn;

    // No overlap
    if (nodeEndPos < selectionStartPos || nodeStartPos > selectionEndPos) {
      return 0;
    }

    // Node contains selection
    if (nodeStartPos <= selectionStartPos && nodeEndPos >= selectionEndPos) {
      // Calculate how close the node size is to the selection size
      const nodeSize = nodeEndPos - nodeStartPos;
      const selectionSize = selectionEndPos - selectionStartPos;
      const sizeRatio = selectionSize / nodeSize;

      // Higher score for nodes that are closer in size to the selection
      return 80 * sizeRatio;
    }

    // Selection contains node
    if (selectionStartPos <= nodeStartPos && selectionEndPos >= nodeEndPos) {
      return 60;
    }

    // Partial overlap
    return 40;
  };

  // Find the node with the highest match score
  let bestNode: Node | null = null;
  let bestScore = 0;

  const findBestMatchingNode = (node: Node) => {
    const score = calculateMatchScore(node);

    if (score > bestScore) {
      bestScore = score;
      bestNode = node;
    }

    // Continue searching in children
    for (const child of node.children) {
      findBestMatchingNode(child);
    }
  };

  findBestMatchingNode(rootNode);
  return bestNode;
};
